import React, { useEffect, useState } from "react";
import { Button, Group, Loader, Center, Text } from "@mantine/core";
import { IconArrowLeft, IconPrinter } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { url } from "../../settings/linkUrl";
import logo from "../../assets/logo.png";
import "./AdministratorReportPage.css";

interface ChartItem {
  label: string;
  stats: string;
  progress: number;
}

interface GroupRow {
  group_name: string;
  total: number;
  paid: number;
  pending: number;
  not_registered: number;
}

interface MoneyRow {
  name_detail: string;
  register: string;
  money: string;
}

interface SetSizeRow {
  set_size: string;
  label: string;
  total: number;
}

interface SetSizeByProgrameRow {
  vut: string;
  levelk: string;
  programe: string;
  set_size: string;
  total: number;
}

function thaiReportDate() {
  const now = new Date();
  const months = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
  ];
  const day = now.getDate();
  const month = months[now.getMonth()];
  const year = now.getFullYear() + 543;
  return `${day} ${month} พ.ศ. ${year}`;
}

export default function AdministratorReportPage() {
  const [loading, setLoading] = useState(true);
  const [chart, setChart] = useState<ChartItem[]>([]);
  const [byFaculty, setByFaculty] = useState<GroupRow[]>([]);
  const [facultyTotals, setFacultyTotals] = useState<GroupRow | null>(null);
  const [money, setMoney] = useState<MoneyRow[]>([]);
  const [setSizes, setSetSizes] = useState<SetSizeRow[]>([]);
  const [setByPrograme, setSetByPrograme] = useState<SetSizeByProgrameRow[]>([]);
  const [setGrandTotal, setSetGrandTotal] = useState(0);

  const headers = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchJson = (path: string) =>
    fetch(`${url}${path}`, { method: "GET", headers: headers() }).then((r) => {
      if (!r.ok) throw new Error(r.statusText);
      return r.json();
    });

  useEffect(() => {
    Promise.all([
      fetchJson("listname/countchart"),
      fetchJson("listname/countbygroup?group=levelk"),
      fetchJson("listname/countmoney"),
      fetchJson("listname/countsetsize"),
    ])
      .then(([chartData, facultyData, moneyData, sizeData]) => {
        setChart(Array.isArray(chartData) ? chartData : []);
        if (Array.isArray(facultyData?.items)) {
          setByFaculty(facultyData.items);
          setFacultyTotals(facultyData.totals || null);
        }
        setMoney(Array.isArray(moneyData) ? moneyData : []);
        if (Array.isArray(sizeData?.items)) {
          setSetSizes(sizeData.items);
          setSetByPrograme(
            Array.isArray(sizeData.items_by_programe)
              ? sizeData.items_by_programe
              : []
          );
          setSetGrandTotal(Number(sizeData.grand_total) || 0);
        }
      })
      .catch(() => {
        /* แสดงรายงานว่างถ้าโหลดไม่สำเร็จ */
      })
      .finally(() => setLoading(false));
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const paidItem = chart.find((c) => c.label.includes("ชำระเงินแล้ว"));
  const pendingItem = chart.find((c) => c.label.includes("ระหว่างชำระ"));
  const notRegItem = chart.find((c) => c.label.includes("ยังไม่ลงทะเบียน"));

  if (loading) {
    return (
      <Center py="xl">
        <Loader color="blue" />
        <Text ml="sm" c="dimmed">
          กำลังจัดทำรายงาน...
        </Text>
      </Center>
    );
  }

  return (
    <div className="admin-report-page">
      <Group className="admin-report-toolbar no-print" justify="space-between" mb="md">
        <Button
          component={Link}
          to="/administrator"
          variant="default"
          leftSection={<IconArrowLeft size={16} />}
        >
          กลับหน้าหลัก
        </Button>
        <Button
          color="blue"
          leftSection={<IconPrinter size={16} />}
          onClick={handlePrint}
        >
          พิมพ์รายงาน
        </Button>
      </Group>

      <article className="admin-report-document">
        <header className="admin-report-header">
          <img
            src={logo}
            alt="ตราสัญลักษณ์ มหาวิทยาลัยราชภัฏกำแพงเพชร"
            className="admin-report-logo"
          />
          <h1>มหาวิทยาลัยราชภัฏกำแพงเพชร</h1>
          <h2>
            รายงานสรุปการลงทะเบียนซ้อมรับพระราชทานปริญญาบัตร
            <br />
            (เสนอผู้บริหาร)
          </h2>
          <p className="admin-report-meta">วันที่จัดทำรายงาน {thaiReportDate()}</p>
        </header>

        <section className="admin-report-section">
          <h3>1. ภาพรวมสถานะการลงทะเบียน</h3>
          <div className="admin-report-summary-grid">
            <div className="admin-report-summary-item">
              <div className="label">ลงทะเบียนและชำระเงินแล้ว</div>
              <div className="value">{paidItem?.stats || "—"}</div>
            </div>
            <div className="admin-report-summary-item">
              <div className="label">อยู่ระหว่างชำระเงิน</div>
              <div className="value">{pendingItem?.stats || "—"}</div>
            </div>
            <div className="admin-report-summary-item">
              <div className="label">ยังไม่ลงทะเบียน</div>
              <div className="value">{notRegItem?.stats || "—"}</div>
            </div>
          </div>
        </section>

        <section className="admin-report-section">
          <h3>2. สรุปจำนวนบัณฑิตแยกตามคณะ</h3>
          <table className="admin-report-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>คณะ</th>
                <th>ทั้งหมด</th>
                <th>ชำระแล้ว</th>
                <th>รอชำระ</th>
                <th>ยังไม่ลงทะเบียน</th>
              </tr>
            </thead>
            <tbody>
              {byFaculty.map((row, i) => (
                <tr key={row.group_name}>
                  <td className="num">{i + 1}</td>
                  <td>{row.group_name}</td>
                  <td className="num">{row.total.toLocaleString("th-TH")}</td>
                  <td className="num">{row.paid.toLocaleString("th-TH")}</td>
                  <td className="num">{row.pending.toLocaleString("th-TH")}</td>
                  <td className="num">{row.not_registered.toLocaleString("th-TH")}</td>
                </tr>
              ))}
              {facultyTotals ? (
                <tr className="total-row">
                  <td className="num">—</td>
                  <td>รวมทั้งหมด</td>
                  <td className="num">{facultyTotals.total.toLocaleString("th-TH")}</td>
                  <td className="num">{facultyTotals.paid.toLocaleString("th-TH")}</td>
                  <td className="num">{facultyTotals.pending.toLocaleString("th-TH")}</td>
                  <td className="num">
                    {facultyTotals.not_registered.toLocaleString("th-TH")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>

        <section className="admin-report-section">
          <h3>3. สรุปยอดเงินตามรายการ (จากระบบการเงิน)</h3>
          <table className="admin-report-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>รายการ</th>
                <th>จำนวน (คน)</th>
                <th>ยอดเงิน (บาท)</th>
              </tr>
            </thead>
            <tbody>
              {money.map((row, i) => (
                <tr key={`${row.name_detail}-${i}`}>
                  <td className="num">{i + 1}</td>
                  <td>{row.name_detail}</td>
                  <td className="num">{row.register}</td>
                  <td className="right">{row.money}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="admin-report-section">
          <h3>
            4. สรุปไซส์ครุยวิทยฐานะ (ชำระแล้ว ซื้อ/เช่าครุย รวม{" "}
            {setGrandTotal.toLocaleString("th-TH")} คน)
          </h3>
          <table className="admin-report-table" style={{ marginBottom: "1rem" }}>
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>ไซส์</th>
                <th>จำนวน (คน)</th>
              </tr>
            </thead>
            <tbody>
              {setSizes.map((row, i) => (
                <tr key={row.set_size}>
                  <td className="num">{i + 1}</td>
                  <td>{row.label}</td>
                  <td className="num">{row.total.toLocaleString("th-TH")}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {setByPrograme.length > 0 ? (
            <>
              <Text size="sm" fw={600} mb="xs">
                แยกตามหลักสูตร / สาขาวิชา
              </Text>
              <table className="admin-report-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>หลักสูตร (วุฒิ)</th>
                    <th>คณะ</th>
                    <th>สาขาวิชา</th>
                    <th>ไซส์</th>
                    <th>จำนวน</th>
                  </tr>
                </thead>
                <tbody>
                  {setByPrograme.map((row, i) => (
                    <tr key={`${row.programe}-${row.set_size}-${i}`}>
                      <td className="num">{i + 1}</td>
                      <td>{row.vut || "—"}</td>
                      <td>{row.levelk || "—"}</td>
                      <td>{row.programe}</td>
                      <td className="num">{row.set_size}</td>
                      <td className="num">{row.total.toLocaleString("th-TH")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </section>

        <footer className="admin-report-footer">
          <div className="admin-report-sign">
            <div className="line">ผู้จัดทำรายงาน</div>
          </div>
          <div className="admin-report-sign">
            <div className="line">ผู้บริหารที่รับทราบ</div>
          </div>
        </footer>
      </article>
    </div>
  );
}
